/**
 * Create a Bosun user.
 *
 * No password field, deliberately: `auth.create_user` requires one, but Clerk is the sign-in
 * path so it is vestigial. `useCreateUser` generates it and never returns it — see
 * `lib/initialPassword.ts` for why an unrecoverable value is the right answer here.
 *
 * Failures surface the SERVER's wording rather than a guess: `BosunApiError` carries Frappe's
 * `_server_messages`, already decoded by `lib/api/errors.ts`, so "User x@y.z already exists"
 * reaches the person who can act on it.
 */

import { useState } from 'react'
import { Button } from '../../../components/ui'
import { ROLE_LABELS } from '../../../components/SessionProvider'
import { useCreateUser } from '../../../lib/api/useUsers'
import { ROLES, type Role } from '../../../lib/types'

export function AddUserForm() {
  const create = useCreateUser()
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [role, setRole] = useState<Role>('purchaser')

  const canSubmit = email.trim() !== '' && fullName.trim() !== '' && !create.isPending

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (!canSubmit) return
        create.mutate(
          // Lower-cased, not just trimmed: Frappe treats the `User` docname as identity, so
          // `Joe@X.com` and `joe@x.com` would create rows that never match `get_current_user`'s
          // email and quietly defeat the self-row guards in `UserTable`.
          { email: email.trim().toLowerCase(), fullName: fullName.trim(), role },
          { onSuccess: () => { setEmail(''); setFullName('') } },
        )
      }}
    >
      <div className="fieldrow">
        <label htmlFor="new-user-email">Email</label>
        <input
          id="new-user-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="colleague@example.com"
        />
      </div>
      <div className="fieldrow">
        <label htmlFor="new-user-name">Full name</label>
        <input id="new-user-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
      </div>
      <div className="fieldrow">
        <label htmlFor="new-user-role">Role</label>
        <select id="new-user-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </select>
        <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 4 }}>
          Sales is not yet verified against the live server — no user there holds it today (asks
          doc, A5). Purchaser and Purchase Manager are confirmed.
        </div>
      </div>

      <Button type="submit" variant="primary" disabled={!canSubmit}>
        {create.isPending ? 'Adding…' : 'Add user'}
      </Button>

      {create.isError && (
        <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 8 }}>
          {create.error instanceof Error ? create.error.message : 'Could not add that user.'}
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--muted)' }}>
        They sign in with Clerk, so no password is set here. Their access comes from this Waves
        user record — without one, signing in shows "your account isn't set up in Waves yet".
      </div>
    </form>
  )
}
